import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, Users, ChevronRight, Sparkles, CheckCircle, ArrowRight, Zap } from "lucide-react";
import { Badge } from "../../ui/badge";

const timeSlots = ["8:00", "9:00", "10:00", "11:00", "12:00", "1:00", "2:00", "3:00", "4:00"];

const appointments = [
  { id: 1, col: 0, start: 0, span: 2, patient: "Sarah J.", type: "Crown Prep", color: "bg-blue-100 border-blue-300 text-blue-800" },
  { id: 2, col: 0, start: 3, span: 1, patient: "Mike C.", type: "Exam", color: "bg-blue-50 border-blue-200 text-blue-700" },
  { id: 3, col: 0, start: 5, span: 2, patient: "Emily D.", type: "Root Canal", color: "bg-blue-100 border-blue-300 text-blue-800" },
  { id: 4, col: 1, start: 0, span: 1, patient: "John D.", type: "Prophy", color: "bg-sky-100 border-sky-300 text-sky-800" },
  { id: 5, col: 1, start: 1, span: 1, patient: "Lisa M.", type: "Prophy", color: "bg-sky-100 border-sky-300 text-sky-800" },
  { id: 6, col: 1, start: 2, span: 1, patient: "Rob K.", type: "Perio Maint", color: "bg-sky-50 border-sky-200 text-sky-700" },
  { id: 7, col: 1, start: 4, span: 1, patient: "Amy R.", type: "Prophy", color: "bg-sky-100 border-sky-300 text-sky-800" },
  { id: 8, col: 1, start: 5, span: 1, patient: "Tom B.", type: "Prophy", color: "bg-sky-100 border-sky-300 text-sky-800" },
  { id: 9, col: 2, start: 1, span: 2, patient: "Nina P.", type: "Bridge", color: "bg-indigo-100 border-indigo-300 text-indigo-800" },
  { id: 10, col: 2, start: 4, span: 1, patient: "Dave L.", type: "Filling", color: "bg-indigo-50 border-indigo-200 text-indigo-700" },
  { id: 11, col: 2, start: 6, span: 2, patient: "Kate S.", type: "Veneer Prep", color: "bg-indigo-100 border-indigo-300 text-indigo-800" },
];

const newAppt = { patient: "New Patient", type: "Emergency Exam", time: "11:00 AM", op: "Op 1" };

const PMSSchedulingSlide = () => {
  const [showNewAppt, setShowNewAppt] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const cycle = async () => {
      while (true) {
        setShowNewAppt(false);
        setStep(0);
        await new Promise((r) => setTimeout(r, 3000));
        setStep(1);
        await new Promise((r) => setTimeout(r, 2000));
        setStep(2);
        await new Promise((r) => setTimeout(r, 1500));
        setShowNewAppt(true);
        setStep(3);
        await new Promise((r) => setTimeout(r, 5000));
      }
    };
    cycle();
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute top-[40%] left-[30%] w-[300px] h-[300px] bg-sky-100 rounded-full blur-[80px] opacity-40" />
      </div>

      <div className="flex flex-row h-full w-full max-w-[1600px] gap-10 relative z-10">
        {/* Left Side: Context */}
        <div className="w-[38%] flex flex-col justify-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <Calendar className="w-4 h-4" />
              <span>PMS Feature</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              Intelligent{" "}
              <span className="text-blue-600">Scheduling</span> built for dental
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              A scheduling system that understands operatory management, provider
              assignments, time blocks, and real-time availability. Drag-and-drop
              appointments with color-coded types across multiple providers.
            </p>
          </motion.div>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2">
            {[
              "Drag & Drop",
              "Multi-Provider",
              "Time Blocks",
              "Color-Coded",
              "Real-Time Sync",
              "Auto-Reminders",
            ].map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                {f}
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="text-3xl font-bold text-blue-600 mb-1">6</div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Calendar Views
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-1">
                <Zap className="w-5 h-5 text-blue-600" />
                <span className="text-3xl font-bold text-blue-600">AI</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Smart Suggestions
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Side: Interactive Schedule Mockup */}
        <div className="w-[62%] h-full flex items-center justify-center relative">
          {/* AI Routing Overlay */}
          <AnimatePresence>
            {step >= 1 && step < 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="absolute z-20 top-4 right-4 bg-slate-900/90 backdrop-blur-md rounded-xl p-4 text-white w-[260px] shadow-2xl border border-white/10"
              >
                <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-2">
                  <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">
                    AI Scheduling
                  </span>
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                </div>

                <div className="space-y-3">
                  <div className={`flex items-center gap-3 transition-opacity duration-500 ${step >= 1 ? "opacity-100" : "opacity-30"}`}>
                    <div className="p-1.5 bg-blue-500/20 rounded-lg text-blue-300">
                      <Users className="w-4 h-4" />
                    </div>
                    <div className="text-xs">
                      <div className="font-medium text-slate-200">New Patient Request</div>
                      <div className="text-slate-400">Emergency Exam - Today</div>
                    </div>
                  </div>

                  <div className={`h-4 w-0.5 bg-slate-700 ml-3.5 transition-colors duration-500 ${step >= 2 ? "bg-blue-500" : ""}`} />

                  <div className={`flex items-center gap-3 transition-opacity duration-500 ${step >= 2 ? "opacity-100" : "opacity-30"}`}>
                    <div className="p-1.5 bg-blue-500/20 rounded-lg text-blue-300">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <div className="text-xs">
                      <div className="font-medium text-slate-200">Finding open slot...</div>
                      <div className="text-slate-400">Op 1, 11:00 AM available</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Success Badge */}
          <AnimatePresence>
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="absolute z-20 top-4 right-4 bg-blue-600 text-white rounded-xl px-4 py-3 shadow-xl flex items-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-bold">Appointment Booked</span>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] relative z-10"
          >
            {/* Header */}
            <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Today's Schedule</h3>
                  <p className="text-xs text-slate-500">Monday, February 15, 2026</p>
                </div>
              </div>
              <div className="flex gap-2">
                {["Day", "Week", "Month"].map((v, i) => (
                  <div
                    key={v}
                    className={`px-3 py-1 rounded-lg text-xs font-bold ${
                      i === 0
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {v}
                  </div>
                ))}
              </div>
            </div>

            {/* Schedule Grid */}
            <div className="flex-1 flex overflow-hidden">
              {/* Time Column */}
              <div className="w-14 border-r border-slate-100 flex-shrink-0">
                <div className="h-10 border-b border-slate-100" />
                {timeSlots.map((t) => (
                  <div
                    key={t}
                    className="h-[calc((100%-40px)/9)] flex items-start justify-end pr-2 pt-1"
                  >
                    <span className="text-[10px] text-slate-400 font-medium">{t}</span>
                  </div>
                ))}
              </div>

              {/* Operatory Columns */}
              {[0, 1, 2].map((colIdx) => (
                <div key={colIdx} className="flex-1 border-r border-slate-100 last:border-r-0 relative">
                  {/* Column Header */}
                  <div className="h-10 border-b border-slate-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-slate-600">
                      {colIdx === 0 ? "Dr. Smith" : colIdx === 1 ? "Hygiene 1" : "Dr. Jones"}
                    </span>
                  </div>

                  {/* Time Grid Lines */}
                  <div className="absolute top-10 left-0 right-0 bottom-0">
                    {timeSlots.map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-full border-t border-slate-50"
                        style={{ top: `${(i / 9) * 100}%` }}
                      />
                    ))}
                  </div>

                  {/* Appointments */}
                  <div className="relative" style={{ height: "calc(100% - 40px)" }}>
                    {appointments
                      .filter((a) => a.col === colIdx)
                      .map((appt) => (
                        <motion.div
                          key={appt.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 + appt.id * 0.05, duration: 0.3 }}
                          className={`absolute left-1 right-1 ${appt.color} rounded-lg border-2 p-1.5 cursor-pointer hover:shadow-md transition-all text-[10px] overflow-hidden`}
                          style={{
                            top: `${(appt.start / 9) * 100}%`,
                            height: `${(appt.span / 9) * 100}%`,
                          }}
                        >
                          <div className="font-bold truncate">{appt.patient}</div>
                          <div className="opacity-75 truncate">{appt.type}</div>
                        </motion.div>
                      ))}

                    {/* New Appointment Animation */}
                    <AnimatePresence>
                      {showNewAppt && colIdx === 0 && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.7, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.7 }}
                          transition={{ type: "spring", stiffness: 300, damping: 20 }}
                          className="absolute left-1 right-1 bg-blue-500 border-2 border-blue-600 rounded-lg p-1.5 text-white text-[10px] shadow-lg shadow-blue-500/30 ring-2 ring-blue-400/50"
                          style={{
                            top: `${(3 / 9) * 100}%`,
                            height: `${(1 / 9) * 100}%`,
                          }}
                        >
                          <div className="font-bold truncate flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            New Patient
                          </div>
                          <div className="opacity-90 truncate">Emergency Exam</div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Status Bar */}
            <div className="h-10 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between px-6">
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded bg-blue-200 border border-blue-400" />
                  <span>Restorative</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded bg-sky-200 border border-sky-400" />
                  <span>Hygiene</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded bg-indigo-200 border border-indigo-400" />
                  <span>Complex</span>
                </div>
              </div>
              <div className="text-xs text-slate-500 font-medium">
                <span className="text-blue-600 font-bold">11</span> appointments today
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PMSSchedulingSlide;
